import "./index.css";

import { createBrowserRouter, RouterProvider, Navigate } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AnalyticsProvider } from "./providers/AnalyticsProvider";

import AuthLayout from "./layouts/AuthLayout";
import AppLayout from "./layouts/AppLayout";
import SettingsLayout from "./layouts/SettingsLayout";
import AdminLayout from "./layouts/AdminLayout";
import AuthGuard from "./guards/AuthGuard";
import NoAuthGuard from "./guards/NoAuthGuard";
import AdminGuard from "./guards/AdminGuard";
import PlanGuard from "./guards/PlanGuard";
import LandingRedirect from "./components/common/LandingRedirect";

import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CheckYourEmail from "./pages/CheckYourEmail";

import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import Tdm from "./pages/Tdm";
import WorkAdvisory from "./pages/WorkAdvisory";
import MyContractings from "./pages/MyContractings";
import SuretyPanel from "./pages/surety/SuretyPanel";
import SuretyClients from "./pages/surety/SuretyClients";
import SuretyMarket from "./pages/surety/SuretyMarket";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import ContractingDetail from "./pages/ContractingDetail";
import AccountSettings from "./pages/settings/AccountSettings";
import PlanSettings from "./pages/settings/PlanSettings";
import ApiKeys from "./pages/settings/ApiKeys";
import DocumentsSettings from "./pages/settings/DocumentsSettings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMetrics from "./pages/admin/AdminMetrics";
import AdminContractingErrors from "./pages/admin/AdminContractingErrors";
import AdminBidDocuments from "./pages/admin/AdminBidDocuments";
import AdminMissingFiles from "./pages/admin/AdminMissingFiles";
import AdminWorkAdvisory from "./pages/admin/AdminWorkAdvisory";
import AdminCompanyDocuments from "./pages/admin/AdminCompanyDocuments";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const COMPANY_ROUTES = [
  { path: "companies", element: <Companies /> },
  { path: "companies/:id", element: <CompanyDetail /> },
];

const router = createBrowserRouter([
  {
    path: "",
    element: <NoAuthGuard />,
    children: [
      { index: true, element: <LandingRedirect /> },
      {
        path: "auth",
        element: <AuthLayout />,
        children: [
          { path: "login", element: <Login /> },
          { path: "register", element: <Register /> },
          { path: "forgot-password", element: <ForgotPassword /> },
          { path: "reset-password", element: <ResetPassword /> },
          { path: "check-your-email", element: <CheckYourEmail /> },
        ],
      },
    ],
  },
  {
    path: "",
    element: <AuthGuard />,
    children: [
      {
        path: "",
        element: <AppLayout />,
        // El plan decide a qué vistas se entra, en los dos sentidos: ver `PlanGuard`.
        children: [
          // Dentro de `AppLayout` (activa la organización y monta los providers),
          // que en esta ruta no pinta su marco: es de pantalla completa.
          { path: "onboarding", element: <Onboarding /> },
          {
            element: <PlanGuard />,
            children: [
              { path: "dashboard", element: <Dashboard /> },
              {
                element: <AdminGuard />,
                children: [
                  { path: "tdm", element: <Tdm /> },
                ],
              },
              { path: "work-advisory", element: <WorkAdvisory /> },
              { path: "work-advisory/:id", element: <WorkAdvisory /> },
              { path: "my-contractings", element: <MyContractings /> },
              // Portal Fianzas: Mi equipo es el mismo tablero, con las etapas de garantía.
              { path: "surety", element: <SuretyPanel /> },
              { path: "surety/team", element: <MyContractings /> },
              { path: "surety/clients", element: <SuretyClients /> },
              { path: "surety/market", element: <SuretyMarket /> },
              ...COMPANY_ROUTES,
              { path: "contracting/:id", element: <ContractingDetail /> },
              {
                path: "settings",
                element: <SettingsLayout />,
                children: [
                  { index: true, element: <Navigate to="account" replace /> },
                  { path: "account", element: <AccountSettings /> },
                  { path: "documents", element: <DocumentsSettings /> },
                  { path: "plan", element: <PlanSettings /> },
                  { path: "api-keys", element: <ApiKeys /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "admin",
    element: <AdminGuard />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/users" replace /> },
          { path: "users", element: <AdminUsers /> },
          { path: "metrics", element: <AdminMetrics /> },
          {
            path: "contractings-errors",
            element: <AdminContractingErrors />,
          },
          {
            path: "contractings-missing-files",
            element: <AdminMissingFiles />,
          },
          { path: "bid-documents", element: <AdminBidDocuments /> },
          { path: "work-advisory", element: <AdminWorkAdvisory /> },
          {
            path: "company-documents",
            element: <AdminCompanyDocuments />,
          },
        ],
      },
    ],
  },
  { path: "*", element: <LandingRedirect /> },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AnalyticsProvider>
        <RouterProvider router={router} />
      </AnalyticsProvider>
    </QueryClientProvider>
  );
}

export default App;
